// Form Yöneticisi — plan §5 (S2: tam CRUD + 9 soru tipi editör/preview).
// S1 framework/version iskeleti + S2 section/question dolu.

import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type {
  DerivedSectorPackage,
  FrameworkDetail,
  FrameworkSummary,
  Question,
  QuestionType,
  SectionWithQuestions,
  SectorBlock,
} from "@mahir/shared";
import { QUESTION_TYPES, effectivePeriods } from "@mahir/shared";
import { api, ApiError } from "../../api/client";
import type { SettingListResponse } from "@mahir/shared";
import {
  createFramework,
  forkFrameworkVersion,
  getFramework,
  updateFramework,
  getVersionEditability,
  listFrameworks,
  listSectorBlocks,
  publishVersion as publishVersionApi,
  validatePublish,
  type VersionEditabilityResponse,
} from "../../api/frameworks";
import type { PublishValidationResult } from "@mahir/shared";
import {
  resolveSelection,
  useFormManagerStore,
  type QuestionPatch,
  type SaveState,
} from "../../stores/form-manager-store";
import { useAuthStore } from "../../stores/auth-store";
import { QuestionTypeEditor } from "../../components/form-manager/question-type-editors";
import { QuestionPreview } from "../../components/form-manager/question-type-previews";
import { QuestionTypeIcon } from "../../components/form-manager/question-type-icons";
import { PromptEditor } from "../../components/form-manager/PromptEditor";
import { TransferMenu, type ImportedInfo } from "../../components/form-manager/TransferMenu";
import { PeriodChips } from "../../components/form-manager/PeriodChips";
import { MappingHintsInput } from "../../components/form-manager/MappingHintsInput";
import {
  PERIOD_COPY,
  annualExcludedQuestions,
  describeEffectivePeriods,
  fillTemplate,
  formatOutsideNote,
  formatPeriodBadge,
  hasNoPeriodNarrowing,
  outsideCeiling,
} from "../../lib/period-ui";
import { FRAMEWORK_UI_COPY } from "../../lib/framework-ui-copy";

export function FormManager(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const canCreate = user?.permissions["framework.create"] === true;
  const canEdit = user?.permissions["framework.edit"] === true;

  const [frameworks, setFrameworks] = useState<FrameworkSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  // Kit D7 — import sonrası 6 sn görünen başarı bandı (toast bileşeni yok; QueuePauseBanner deseni)
  const [importSuccess, setImportSuccess] = useState<ImportedInfo | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FrameworkDetail | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // run2 D1 — framework ADI düzenleme: içe aktarmada ad DOSYADAN gelir ("… (run2)"
  // demek için UI yoktu, kullanıcı tarayıcıdan PATCH atmak zorunda kalmıştı). Satır içi
  // düzenleme, mevcut `PATCH /admin/frameworks/:id` ile (ad versiyona değil FRAMEWORK'e
  // ait — yayında sürümde de serbesttir, sürüm kilidi kullanmaz).
  const [renameValue, setRenameValue] = useState<string | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [devModeJsonPanel, setDevModeJsonPanel] = useState(false);
  // M13 S7: orphan-published edit/delete eligibility
  const [editability, setEditability] = useState<VersionEditabilityResponse | null>(null);

  // M8-S4 — Dev Mode flag (ui.dev_mode_json_panel). Framework admin'leri genelde
  // system.admin izinine sahip; değilse 403'ü sessiz yut + false default kalsın.
  useEffect(() => {
    let aborted = false;
    api<SettingListResponse>("/admin/settings")
      .then((res) => {
        if (aborted) return;
        const flag = res.settings.find((s) => s.key === "ui.dev_mode_json_panel");
        setDevModeJsonPanel(flag?.value === "true");
      })
      .catch(() => {
        /* system.admin izni yoksa 403 — default false */
      });
    return () => {
      aborted = true;
    };
  }, []);

  // Zustand: selector'ları ayrı ayrı al (full state alma re-render spam yapar).
  const storeLoad = useFormManagerStore((s) => s.load);
  const versionStatus = useFormManagerStore((s) => s.versionStatus);
  const loading = useFormManagerStore((s) => s.loading);
  const saveState = useFormManagerStore((s) => s.saveState);
  const saveMessage = useFormManagerStore((s) => s.saveMessage);

  async function refreshList() {
    setListLoading(true);
    try {
      const res = await listFrameworks();
      setFrameworks(res.frameworks);
      setListError(null);
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Liste yüklenemedi");
    } finally {
      setListLoading(false);
    }
  }

  async function refreshDetail(id: string) {
    try {
      const d = await getFramework(id);
      setDetail(d);
      setSelectedVersionId((prev) => {
        if (prev && d.versions.some((v) => v.id === prev)) return prev;
        return d.currentVersion?.id ?? d.versions[0]?.id ?? null;
      });
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Detay yüklenemedi");
    }
  }

  async function onRename(): Promise<void> {
    const next = (renameValue ?? "").trim();
    if (!detail || renameBusy) return;
    if (next.length < 2 || next === detail.name) {
      setRenameValue(null);
      return;
    }
    setRenameBusy(true);
    try {
      await updateFramework(detail.id, { name: next });
      await refreshDetail(detail.id);
      await refreshList();
      setRenameValue(null);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : "Çerçeve adı güncellenemedi");
    } finally {
      setRenameBusy(false);
    }
  }

  useEffect(() => {
    void refreshList();
  }, []);

  useEffect(() => {
    // Framework değişince önce version'u sıfırla — aksi halde eski framework'ün
    // versionId'siyle store.load tetiklenip 404 döner (race).
    setSelectedVersionId(null);
    setDetail(null);
    if (selectedId) {
      void refreshDetail(selectedId);
    }
  }, [selectedId]);

  // Version değişince store'u yükle
  useEffect(() => {
    if (selectedId && selectedVersionId) {
      void storeLoad(selectedId, selectedVersionId);
      // M13 S7: editability lookup
      getVersionEditability(selectedId, selectedVersionId)
        .then(setEditability)
        .catch(() => setEditability(null));
    } else {
      setEditability(null);
    }
  }, [selectedId, selectedVersionId]);

  async function onFork() {
    if (!detail) return;
    // #010 Fork friction — confirm modal yerine native confirm (basit ama efektif).
    // Yanlışlıkla v2 yaratma riskini azaltır.
    const ok = window.confirm(
      "Bu çerçeve için yeni bir versiyon (taslak) oluşturulacak.\n\n" +
        "Mevcut yayınlanmış versiyon değişmez; yeni taslakta düzenleme yaparsınız.\n" +
        "Yeni versiyonu sonra yayınlayabilir veya kullanmıyorsanız silebilirsiniz.\n\n" +
        "Devam edilsin mi?",
    );
    if (!ok) return;
    try {
      const v = await forkFrameworkVersion(detail.id);
      await refreshDetail(detail.id);
      await refreshList();
      setSelectedVersionId(v.id);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Fork başarısız");
    }
  }

  // #008 Framework silme handler
  async function onDeleteFramework() {
    if (!detail) return;
    const ok = window.confirm(
      `"${detail.name}" çerçevesini silmek istediğinize emin misiniz?\n\n` +
        "• Bu çerçeve hiçbir projede kullanılmıyorsa silinir.\n" +
        "• Kullanılıyorsa hata mesajı görürsünüz.\n" +
        "• Soft delete: kayıt DB'de kalır, listede görünmez.",
    );
    if (!ok) return;
    try {
      const { deleteFramework: del } = await import("../../api/frameworks");
      await del(detail.id);
      await refreshList();
      // Liste'ye dön
      setDetail(null);
      setSelectedId(null);
      setSelectedVersionId(null);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Silme başarısız");
    }
  }

  // #023 Fork (version) silme handler
  async function onDeleteVersion() {
    if (!detail || !selectedVersionId) return;
    const v = detail.versions.find((x) => x.id === selectedVersionId);
    if (!v) return;
    // M13 S7: orphan-published da silinebilir.
    const statusLine =
      v.status === "draft"
        ? "• Bu bir taslak — soft delete uygulanacak."
        : v.status === "published"
          ? `• "Yayında" sürüm — proje ve sektör paketleri tarafından kullanılmadığı için silinebilir (orphan published).`
          : "• Arşivlenmiş sürümler silinemez (yeni fork edin).";
    const ok = window.confirm(
      `"${v.versionNumber}" versiyonunu silmek istediğinize emin misiniz?\n\n` +
        statusLine +
        `\n• Bir projede veya sektör paketinde kullanılıyorsa silinmez (409 in_use).\n` +
        `• Soft delete: kayıt DB'de kalır (deleted_at set).`,
    );
    if (!ok) return;
    try {
      const { deleteFrameworkVersion: delV } = await import("../../api/frameworks");
      await delV(detail.id, selectedVersionId);
      await refreshDetail(detail.id);
      // Silinen version artık listede yok; en son version'a geç
      const refreshed = await getFramework(detail.id);
      if (refreshed.versions.length > 0) {
        setSelectedVersionId(refreshed.versions[0]!.id);
      } else {
        setSelectedVersionId(null);
      }
      await refreshList();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Versiyon silinemedi");
    }
  }

  const [publishState, setPublishState] = useState<PublishDialogState>({
    open: false,
    validation: null,
    publishing: false,
    error: null,
    blocks: null,
    selectedBlocks: [],
  });
  // Yayın sonrası görünen türetme bandı (importSuccess deseni — toast bileşeni yok).
  // run2 D2: 8 sn yetmiyordu, bant 20 sn durur ve × ile kapatılabilir.
  const [derivedInfo, setDerivedInfo] = useState<DerivedSectorPackage[] | null>(null);
  // Yayınla modalının istek nöbeti — bkz. onPublishClick.
  const publishRequestRef = useRef(0);

  async function onPublishClick() {
    if (!detail || !selectedVersionId) return;
    setPublishState({
      open: true,
      validation: null,
      publishing: false,
      error: null,
      blocks: null,
      selectedBlocks: [],
    });
    const frameworkId = detail.id;
    const versionId = selectedVersionId;
    // Nöbet: kullanıcı modal açıkken sürüm/framework değiştirirse geç gelen yanıt
    // bayat blok listesini modale yazmasın (inceleme bulgusu).
    const token = ++publishRequestRef.current;
    const isStale = (): boolean => publishRequestRef.current !== token;
    try {
      const v = await validatePublish(frameworkId, versionId);
      if (isStale()) return;
      setPublishState((p) => ({ ...p, validation: v }));
    } catch (err) {
      if (isStale()) return;
      setPublishState((p) => ({
        ...p,
        error: err instanceof ApiError ? err.message : "Doğrulama başarısız",
      }));
    }
    // Sektör blokları ayrı istek: listesi boşsa modalde bölüm hiç görünmez.
    // Hata halinde de modal çalışır (blocks = [] → türetme bölümü yok).
    const res = await listSectorBlocks(frameworkId, versionId).catch(() => ({ blocks: [] }));
    if (isStale()) return;
    setPublishState((p) => ({
      ...p,
      blocks: res.blocks,
      // Varsayılan: her blok işaretli — kullanıcı istemediğini kaldırır.
      selectedBlocks: res.blocks.map((b) => b.sectionCode),
    }));
  }

  async function onPublishConfirm() {
    if (!detail || !selectedVersionId) return;
    setPublishState((p) => ({ ...p, publishing: true, error: null }));
    try {
      const res = await publishVersionApi(
        detail.id,
        selectedVersionId,
        publishState.selectedBlocks,
      );
      await refreshDetail(detail.id);
      await refreshList();
      // #007 fix: publish sonrası auto-fork kaldırıldı.
      // Aynı version'da kal — published olduğu için read-only modda görünür.
      // Yeni versiyon istiyorsa kullanıcı "Yeni Versiyon (Fork)" butonu kullanır.
      setSelectedVersionId(res.publishedVersion.id);
      setPublishState({
        open: false,
        validation: null,
        publishing: false,
        error: null,
        blocks: null,
        selectedBlocks: [],
      });
      if (res.sectorPackages.length > 0) {
        setDerivedInfo(res.sectorPackages);
        // run2 D2 — 8 sn bant kolay kaçırılıyordu (koşumda hiç yakalanamadı). Bant
        // kullanıcı kapatana ya da `DERIVED_BAND_MS`'e kadar durur.
        window.setTimeout(() => setDerivedInfo(null), DERIVED_BAND_MS);
      }
    } catch (err) {
      setPublishState((p) => ({
        ...p,
        publishing: false,
        error: err instanceof ApiError ? err.message : "Yayınlama başarısız",
      }));
    }
  }

  const isDraft = versionStatus === "draft";
  const canPublish = user?.permissions["framework.publish"] === true;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  return (
    <div
      className="form-manager-grid"
      style={{
        background: "var(--color-bg)",
        fontFamily: "var(--font-sans)",
      }}
    >
      {/* SOL PANEL — kompakt header + geniş bölüm ağacı */}
      <aside
        style={{
          background: "var(--color-surface)",
          borderRight: "1px solid var(--color-border)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header: framework dropdown + versiyon dropdown + yeni/fork/publish */}
        <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--color-border)" }}>
          <label
            style={{
              fontSize: "0.68rem",
              color: "var(--color-text-3)",
              display: "block",
              marginBottom: 4,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Çerçeve
          </label>
          {/* run2 D1 — satır içi ad düzenleme (yalnız bir framework seçiliyken). */}
          {renameValue !== null && detail ? (
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <input
                autoFocus
                aria-label="Çerçeve adı"
                value={renameValue}
                disabled={renameBusy}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void onRename();
                  if (e.key === "Escape") setRenameValue(null);
                }}
                className="flex-1 min-w-0 px-2 py-1.5 text-sm rounded border"
                style={{ borderColor: "var(--color-border)", background: "var(--color-bg)" }}
              />
              <button
                onClick={() => void onRename()}
                disabled={renameBusy}
                title="Adı kaydet"
                className="px-2.5 py-1.5 text-xs rounded whitespace-nowrap"
                style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
              >
                Kaydet
              </button>
              <button
                onClick={() => setRenameValue(null)}
                disabled={renameBusy}
                title="Vazgeç"
                className="px-2.5 py-1.5 text-xs rounded whitespace-nowrap"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-2)",
                }}
              >
                Vazgeç
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              {/* min-w-0: flex item <select> varsayılan min-width:auto ile en uzun option'a
                (kit dogfood adları 60+ karakter) büyür, "+ Yeni"yi 280px panelin dışına iter
                (Finar 2026-09-24). Tarayıcı seçili metni kendi kırpar. */}
              <select
                value={selectedId ?? ""}
                onChange={(e) => setSelectedId(e.target.value || null)}
                className="flex-1 min-w-0 px-2 py-1.5 text-sm rounded border"
                style={{ borderColor: "var(--color-border)", background: "var(--color-bg)" }}
                disabled={listLoading}
              >
                <option value="">
                  {listLoading
                    ? "Yükleniyor…"
                    : frameworks.length === 0
                      ? "Henüz yok"
                      : "— seçin —"}
                </option>
                {frameworks.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              {canEdit && detail ? (
                <button
                  onClick={() => setRenameValue(detail.name)}
                  title="Çerçeve adını değiştir"
                  aria-label="Çerçeve adını değiştir"
                  className="px-2.5 py-1.5 text-xs rounded whitespace-nowrap"
                  style={{
                    background: "transparent",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-primary)",
                  }}
                >
                  ✎ Ad
                </button>
              ) : null}
              {canCreate ? (
                <button
                  onClick={() => setCreateOpen(true)}
                  title="Yeni Çerçeve"
                  className="px-2.5 py-1.5 text-xs rounded whitespace-nowrap"
                  style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
                >
                  + Yeni
                </button>
              ) : null}
            </div>
          )}

          {detail ? (
            <>
              <label
                style={{
                  fontSize: "0.68rem",
                  color: "var(--color-text-3)",
                  display: "block",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Versiyon
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                <select
                  value={selectedVersionId ?? ""}
                  onChange={(e) => setSelectedVersionId(e.target.value || null)}
                  className="flex-1 min-w-0 px-2 py-1.5 text-sm rounded border"
                  style={{ borderColor: "var(--color-border)", background: "var(--color-bg)" }}
                >
                  {detail.versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.versionNumber} · {v.status}
                    </option>
                  ))}
                </select>
                {canEdit ? (
                  <button
                    onClick={() => void onFork()}
                    title="Yeni draft versiyon (mevcut sections + questions kopyalanır)"
                    className="px-2.5 py-1.5 text-xs rounded whitespace-nowrap"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--color-border)",
                      color: "var(--color-primary)",
                    }}
                  >
                    + Fork
                  </button>
                ) : null}
                {/* M13 S7 — Sürüm silme.
                    Draft: her zaman silinebilir (last_version koruması backend'de).
                    Published: sadece orphan ise (editability.editable === true). */}
                {canEdit &&
                ((isDraft && detail.versions.length > 1) ||
                  (editability?.status === "published" && editability.editable)) ? (
                  <button
                    onClick={() => void onDeleteVersion()}
                    title={
                      isDraft
                        ? "Bu taslak versiyonu sil"
                        : "Bu yayında sürüm orphan (proje/paket yok) — silinebilir"
                    }
                    className="px-2.5 py-1.5 text-xs rounded whitespace-nowrap"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--color-error)",
                      color: "var(--color-error)",
                    }}
                  >
                    🗑
                  </button>
                ) : null}
              </div>
              {/* M13 S7 — Orphan-published banner.
                  Eligible published: bilgilendirici warning (düzenlenebilir + kilitlenir).
                  Ineligible published: kilitli banner (proje/paket sayısı). */}
              {editability?.status === "published" && (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 10px",
                    borderRadius: 6,
                    fontSize: "0.78rem",
                    lineHeight: 1.4,
                    background: editability.editable
                      ? "color-mix(in srgb, var(--color-warning, #f0ad4e) 10%, white)"
                      : "color-mix(in srgb, var(--color-text-3) 10%, white)",
                    border: editability.editable
                      ? "1px solid var(--color-warning, #f0ad4e)"
                      : "1px solid var(--color-border)",
                    color: editability.editable ? "var(--color-text)" : "var(--color-text-2)",
                  }}
                >
                  {editability.editable ? (
                    <>
                      <strong>🟡 Yayında — düzenlenebilir.</strong> Henüz proje veya sektör paketine
                      bağlı değil. Bir proje açılırsa düzenleme otomatik kilitlenir.
                    </>
                  ) : (
                    <>
                      <strong>🔒 Kilitli.</strong>{" "}
                      {editability.projectCount > 0 &&
                        `${editability.projectCount.toString()} projede`}
                      {editability.projectCount > 0 && editability.sectorPackageCount > 0 && " + "}
                      {editability.sectorPackageCount > 0 &&
                        `${editability.sectorPackageCount.toString()} pakette`}{" "}
                      kullanılıyor — yayında sürüm değişiklikleri korunuyor.
                    </>
                  )}
                </div>
              )}
              {canPublish && isDraft ? (
                <button
                  onClick={() => void onPublishClick()}
                  title="Bu draft versiyonu yayınla"
                  className="w-full mt-2 px-3 py-2 text-sm rounded font-semibold"
                  style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
                >
                  Yayınla
                </button>
              ) : null}
              {/* #008 — Soft delete butonu. canEdit yetkisi yeterli (framework.edit). */}
              {canEdit ? (
                <button
                  onClick={() => void onDeleteFramework()}
                  title="Çerçeveyi sil (sadece hiçbir projede kullanılmıyorsa)"
                  className="w-full mt-2 px-3 py-1.5 text-xs rounded"
                  style={{
                    background: "transparent",
                    border: "1px solid var(--color-error)",
                    color: "var(--color-error)",
                  }}
                >
                  🗑 Çerçeveyi Sil
                </button>
              ) : null}
            </>
          ) : null}

          {/* Aktarım dropdown — dışa/içe aktar (import sonrası liste reload + yeni framework seç) */}
          <div style={{ marginTop: 8 }}>
            {derivedInfo &&
              (() => {
                // Hiçbiri türetilemediyse yeşil "0 türetildi" YANILTICI olur: bant
                // amber uyarıya döner ve sebepleri yazar (inceleme bulgusu).
                const ok = derivedInfo.filter((d) => d.status === "derived");
                const skipped = derivedInfo.filter((d) => d.status !== "derived");
                const allFailed = ok.length === 0;
                return (
                  <div
                    role="status"
                    className="mb-2 px-3 py-2 rounded text-xs"
                    style={{
                      background: allFailed
                        ? "var(--color-warning-bg)"
                        : "color-mix(in srgb, var(--color-success) 10%, white)",
                      border: `1px solid ${
                        allFailed ? "var(--color-warning-border)" : "var(--color-border)"
                      }`,
                      color: allFailed ? "var(--color-warning-text)" : "var(--color-text)",
                      lineHeight: 1.5,
                    }}
                  >
                    {allFailed ? (
                      <>
                        <strong>Sektör paketi türetilemedi</strong>
                        {`: ${skipped
                          .map((d) => `${d.name} (${d.message ?? "atlandı"})`)
                          .join(
                            ", ",
                          )}. Çerçeve yayınlandı; paketleri paket ekranından elle açabilirsiniz. `}
                      </>
                    ) : (
                      <>
                        <strong>{ok.length} sektör paketi türetildi</strong>
                        {` · ${ok.map((d) => d.name).join(", ")}`}
                        {skipped.length > 0 &&
                          ` · atlanan: ${skipped
                            .map((d) => `${d.name} (${d.message ?? "atlandı"})`)
                            .join(", ")}`}
                        {". "}
                      </>
                    )}
                    <Link
                      to="/admin/frameworks/sector-packages"
                      style={{ color: "var(--color-primary)", textDecoration: "underline" }}
                    >
                      Sektör paketleri
                    </Link>
                    {/* run2 D2 — kapatma düğmesi: bant kendi kendine kaybolmadan önce
                        kullanıcı okuyup kapatabilir (TransferMenu başarı bandı emsali). */}
                    <button
                      type="button"
                      aria-label="Bildirimi kapat"
                      title="Kapat"
                      onClick={() => setDerivedInfo(null)}
                      style={{
                        float: "right",
                        background: "none",
                        border: "none",
                        padding: 0,
                        marginLeft: 8,
                        cursor: "pointer",
                        color: "inherit",
                        font: "inherit",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })()}
            {importSuccess && (
              <div
                role="status"
                className="mb-2 px-3 py-2 rounded text-xs"
                style={{
                  background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text)",
                  lineHeight: 1.5,
                }}
              >
                {importSuccess.mode === "version" ? (
                  <>
                    <strong>
                      Yeni sürüm {importSuccess.versionNumber ?? "(numarasız)"} taslak olarak açıldı
                    </strong>{" "}
                    · {importSuccess.sectionCount} bölüm · {importSuccess.questionCount} soru.
                    Şimdi: bölüm prompt'larını gözden geçirin → Yayınla.
                  </>
                ) : (
                  <>
                    <strong>İçe aktarıldı</strong> · {importSuccess.sectionCount} bölüm ·{" "}
                    {importSuccess.questionCount} soru · taslak. Şimdi: bölüm prompt'larını gözden
                    geçirin → Yayınla.
                  </>
                )}
                {importSuccess.qualityWarnings > 0 &&
                  ` · ${importSuccess.qualityWarnings} kalite uyarısı (içerik atılmadı, bilgi).`}
                {importSuccess.warningsIgnored > 0 &&
                  ` · ${importSuccess.warningsIgnored} uyarı yok sayıldı (bilinmeyen alanlar atıldı).`}
              </div>
            )}
            <TransferMenu
              frameworkId={detail?.id ?? null}
              versionId={selectedVersionId}
              onImported={(newId, info) => {
                setImportSuccess(info);
                window.setTimeout(() => setImportSuccess(null), 6000);
                void (async () => {
                  await refreshList();
                  setSelectedId(newId);
                  if (info.mode === "version") {
                    // FW-04: hedef framework zaten seçili → selectedId effect'i tetiklenmez.
                    // Detayı elle tazeleyip yeni taslak sürüme geçiyoruz.
                    await refreshDetail(newId);
                    if (info.versionId) setSelectedVersionId(info.versionId);
                  }
                })();
              }}
            />
          </div>

          {listError ? (
            <div
              className="mt-3 px-3 py-2 rounded text-xs"
              style={{
                background: "color-mix(in srgb, var(--color-error) 10%, white)",
                color: "var(--color-error)",
              }}
            >
              {listError}
            </div>
          ) : null}
        </div>

        {/* Bölümler tree — sol panelin geri kalanını kaplar */}
        <div style={{ overflowY: "auto", flex: 1, padding: "10px 14px" }}>
          {!detail ? (
            <p className="text-sm" style={{ color: "var(--color-text-3)" }}>
              Bir çerçeve seçin veya yeni oluşturun.
            </p>
          ) : (
            <SectionTree canEdit={canEdit && isDraft} />
          )}
        </div>

        <SaveIndicator state={saveState} message={saveMessage} />
      </aside>

      {/* ORTA PANEL */}
      <main style={{ overflow: "auto", padding: 0, position: "relative" }}>
        {!detail ? (
          <div style={{ padding: 32 }}>
            <EmptyState
              title="Bir çerçeve seçin"
              body="Sol panelden çerçeve seçin veya yeni bir çerçeve oluşturun."
            />
          </div>
        ) : loading ? (
          <p style={{ padding: 32, color: "var(--color-text-2)" }}>Yükleniyor…</p>
        ) : (
          <>
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 5,
                background: "var(--color-bg)",
                borderBottom: "1px solid var(--color-border)",
                padding: "10px 32px",
                display: "flex",
                gap: 6,
                alignItems: "center",
              }}
            >
              <button
                onClick={() => setPreviewMode(false)}
                className="px-3 py-1.5 text-sm rounded"
                style={{
                  background: !previewMode ? "var(--color-primary)" : "transparent",
                  color: !previewMode ? "var(--color-surface)" : "var(--color-text-2)",
                  border: !previewMode ? "none" : "1px solid var(--color-border)",
                }}
              >
                ✎ Editör
              </button>
              <button
                onClick={() => setPreviewMode(true)}
                className="px-3 py-1.5 text-sm rounded"
                style={{
                  background: previewMode ? "var(--color-primary)" : "transparent",
                  color: previewMode ? "var(--color-surface)" : "var(--color-text-2)",
                  border: previewMode ? "none" : "1px solid var(--color-border)",
                }}
              >
                👁 Tüm Formu Önizle
              </button>
              {/* #005 — Sticky tab bar'a save state göstergesi. Middle panel scroll
                  edilirken kullanıcı her zaman "kaydedildi mi?" sorusuna cevap görür. */}
              <div style={{ marginLeft: "auto" }}>
                <SaveIndicatorInline state={saveState} message={saveMessage} />
              </div>
            </div>
            <div style={{ padding: 32 }}>
              {previewMode ? (
                <FormPreview />
              ) : (
                <MiddlePanel
                  detailName={detail.name}
                  canEdit={canEdit}
                  isDraft={isDraft}
                  quarterlyProjectCount={detail.quarterlyProjectCount}
                  // Yalnız detay tazelenir — refreshList sol rail'i boş yanıp söndürüyordu.
                  onFrameworkChanged={() => refreshDetail(detail.id)}
                />
              )}
            </div>
          </>
        )}
      </main>

      {/* SAĞ PANEL — tablet'te drawer */}
      <aside
        className="form-manager-right"
        data-open={drawerOpen ? "true" : "false"}
        style={{
          background: "var(--color-surface)",
          borderLeft: "1px solid var(--color-border)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <RightPanel onClose={() => setDrawerOpen(false)} devMode={devModeJsonPanel} />
      </aside>

      {/* Tablet drawer açma butonu — CSS ile sadece <1024px'te görünür */}
      <button
        className="form-manager-drawer-toggle"
        onClick={() => setDrawerOpen((s) => !s)}
        style={{
          position: "fixed",
          right: 12,
          bottom: 12,
          padding: "10px 14px",
          borderRadius: 999,
          background: "var(--color-primary)",
          color: "var(--color-surface)",
          border: "none",
          fontSize: "0.85rem",
          boxShadow: "var(--shadow-md)",
          cursor: "pointer",
          zIndex: 50,
        }}
      >
        {drawerOpen ? "Kapat" : "Önizleme / JSON"}
      </button>

      {createOpen ? (
        <CreateFrameworkModal
          onClose={() => setCreateOpen(false)}
          onSuccess={async (id) => {
            setCreateOpen(false);
            await refreshList();
            setSelectedId(id);
          }}
        />
      ) : null}

      {saveState === "conflict" ? <ConflictModal /> : null}

      {publishState.open ? (
        <PublishDialog
          state={publishState}
          onClose={() =>
            setPublishState({
              open: false,
              validation: null,
              publishing: false,
              error: null,
              blocks: null,
              selectedBlocks: [],
            })
          }
          onConfirm={() => void onPublishConfirm()}
          onToggleBlock={(code) =>
            setPublishState((p) => ({
              ...p,
              selectedBlocks: p.selectedBlocks.includes(code)
                ? p.selectedBlocks.filter((c) => c !== code)
                : [...p.selectedBlocks, code],
            }))
          }
        />
      ) : null}
    </div>
  );
}

function sectionMoveBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "2px 4px",
    fontSize: "0.65rem",
    lineHeight: 1,
    background: "transparent",
    border: "1px solid var(--color-border)",
    borderRadius: 3,
    color: disabled ? "var(--color-text-3)" : "var(--color-primary)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.35 : 1,
  };
}

function SectionTree({ canEdit }: { canEdit: boolean }): JSX.Element | null {
  const tree = useFormManagerStore((s) => s.tree);
  const selection = useFormManagerStore((s) => s.selection);
  const select = useFormManagerStore((s) => s.select);
  const addSection = useFormManagerStore((s) => s.addSection);
  const moveSection = useFormManagerStore((s) => s.moveSection);
  const reorderSections = useFormManagerStore((s) => s.reorderSections);

  const [dragSectionId, setDragSectionId] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Seçili bölüm / seçili sorunun bölümü otomatik expand olsun
  useEffect(() => {
    if (!tree) return;
    if (selection.kind === "section") {
      setExpanded((prev) => (prev.has(selection.id) ? prev : new Set(prev).add(selection.id)));
    } else if (selection.kind === "question") {
      const parent = tree.sections.find((s) => s.questions.some((q) => q.id === selection.id));
      if (parent)
        setExpanded((prev) => (prev.has(parent.id) ? prev : new Set(prev).add(parent.id)));
    }
  }, [tree, selection]);

  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionCode, setNewSectionCode] = useState("");
  const [newSectionLabel, setNewSectionLabel] = useState("");
  // In-flight kilidi (Finar çift-tık bug'ı): istek uçarken ikinci tık aynı bölümü
  // tekrar göndermesin. Ref + state — ref senkron guard (iki tık aynı render'da),
  // state buton disabled görünümü için.
  const addingSectionRef = useRef(false);
  const [addingSection, setAddingSection] = useState(false);

  async function onAddSection() {
    if (!newSectionCode.trim() || !newSectionLabel.trim()) return;
    if (addingSectionRef.current) return;
    addingSectionRef.current = true;
    setAddingSection(true);
    try {
      await addSection(newSectionCode.trim(), newSectionLabel.trim());
    } finally {
      addingSectionRef.current = false;
      setAddingSection(false);
    }
    setNewSectionCode("");
    setNewSectionLabel("");
    setAddSectionOpen(false);
  }

  if (!tree) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          fontSize: "0.7rem",
          color: "var(--color-text-3)",
          marginBottom: 6,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Bölümler</span>
        {canEdit ? (
          <button
            onClick={() => setAddSectionOpen((s) => !s)}
            style={{
              padding: "2px 6px",
              fontSize: "0.68rem",
              border: "1px dashed var(--color-border)",
              borderRadius: 4,
              background: "transparent",
              color: "var(--color-primary)",
              cursor: "pointer",
            }}
          >
            {addSectionOpen ? "İptal" : "+ Bölüm"}
          </button>
        ) : null}
      </div>

      {addSectionOpen ? (
        <div
          style={{
            padding: 8,
            background: "var(--color-bg)",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            marginBottom: 10,
            display: "grid",
            gap: 6,
          }}
        >
          <input
            type="text"
            placeholder="Kod (GRI-302)"
            value={newSectionCode}
            onChange={(e) => setNewSectionCode(e.target.value)}
            style={{
              padding: "5px 8px",
              fontSize: "0.78rem",
              borderRadius: 4,
              border: "1px solid var(--color-border)",
              background: "var(--color-surface)",
            }}
          />
          <input
            type="text"
            placeholder="Bölüm adı"
            value={newSectionLabel}
            onChange={(e) => setNewSectionLabel(e.target.value)}
            style={{
              padding: "5px 8px",
              fontSize: "0.78rem",
              borderRadius: 4,
              border: "1px solid var(--color-border)",
              background: "var(--color-surface)",
            }}
          />
          <button
            onClick={() => void onAddSection()}
            disabled={addingSection || !newSectionCode.trim() || !newSectionLabel.trim()}
            style={{
              padding: "5px 10px",
              fontSize: "0.75rem",
              borderRadius: 4,
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              border: "none",
              cursor: "pointer",
              opacity: addingSection || !newSectionCode.trim() || !newSectionLabel.trim() ? 0.5 : 1,
            }}
          >
            Ekle
          </button>
        </div>
      ) : null}

      {tree.sections.length === 0 ? (
        <div
          style={{
            padding: "10px 12px",
            border: "1px dashed var(--color-border)",
            borderRadius: 6,
            fontSize: "0.78rem",
            color: "var(--color-text-3)",
          }}
        >
          Henüz bölüm yok.
        </div>
      ) : (
        <ul>
          {tree.sections.map((s, idx) => {
            const isOpen = expanded.has(s.id);
            const selected = selection.kind === "section" && selection.id === s.id;
            const isFirst = idx === 0;
            const isLast = idx === tree.sections.length - 1;
            return (
              <li
                key={s.id}
                draggable={canEdit}
                onDragStart={(e) => {
                  setDragSectionId(s.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  if (dragSectionId && dragSectionId !== s.id) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!dragSectionId || dragSectionId === s.id || !tree) return;
                  const ids = tree.sections.map((x) => x.id);
                  const from = ids.indexOf(dragSectionId);
                  const to = ids.indexOf(s.id);
                  if (from < 0 || to < 0) return;
                  const next = [...ids];
                  next.splice(from, 1);
                  next.splice(to, 0, dragSectionId);
                  setDragSectionId(null);
                  void reorderSections(next);
                }}
                onDragEnd={() => setDragSectionId(null)}
                style={{
                  opacity: dragSectionId === s.id ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 0",
                    borderLeft: selected
                      ? "3px solid var(--color-primary)"
                      : "3px solid transparent",
                  }}
                >
                  <button
                    onClick={() => toggle(s.id)}
                    style={{
                      padding: "2px 6px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--color-text-3)",
                      fontSize: "0.8rem",
                    }}
                  >
                    {isOpen ? "▾" : "▸"}
                  </button>
                  <button
                    onClick={() => select({ kind: "section", id: s.id })}
                    style={{
                      flex: 1,
                      textAlign: "left",
                      padding: "2px 0",
                      background: "transparent",
                      border: "none",
                      cursor: canEdit ? "grab" : "pointer",
                      color: selected ? "var(--color-primary)" : "var(--color-text)",
                      fontWeight: selected ? 600 : 400,
                      fontSize: "0.82rem",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.7rem",
                        marginRight: 4,
                        color: "var(--color-text-3)",
                      }}
                    >
                      {s.code}
                    </span>
                    {s.label}
                    <span
                      style={{
                        marginLeft: 4,
                        color: "var(--color-text-3)",
                        fontSize: "0.7rem",
                      }}
                    >
                      ({s.questions.length})
                    </span>
                  </button>
                  {canEdit ? (
                    <div style={{ display: "flex", gap: 2, marginRight: 4 }}>
                      <button
                        onClick={() => void moveSection(s.id, "up")}
                        disabled={isFirst}
                        title="Yukarı taşı"
                        style={sectionMoveBtnStyle(isFirst)}
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => void moveSection(s.id, "down")}
                        disabled={isLast}
                        title="Aşağı taşı"
                        style={sectionMoveBtnStyle(isLast)}
                      >
                        ▼
                      </button>
                    </div>
                  ) : null}
                </div>
                {isOpen ? (
                  <div style={{ marginLeft: 20 }}>
                    {s.questions.map((q) => {
                      const qSelected = selection.kind === "question" && selection.id === q.id;
                      return (
                        <button
                          key={q.id}
                          onClick={() => select({ kind: "question", id: q.id })}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "3px 6px",
                            background: qSelected ? "rgba(15,42,61,0.06)" : "transparent",
                            border: "none",
                            borderLeft: qSelected
                              ? "2px solid var(--color-primary)"
                              : "2px solid transparent",
                            cursor: "pointer",
                            color: "var(--color-text)",
                            fontSize: "0.78rem",
                            opacity: q.enabled ? 1 : 0.45,
                            textDecoration: q.enabled ? "none" : "line-through",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                          title={`${q.code} · ${TYPE_LABELS[q.type]}`}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              color: "var(--color-text-3)",
                              flexShrink: 0,
                            }}
                          >
                            <QuestionTypeIcon type={q.type} size={14} />
                          </span>
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {q.label}
                          </span>
                        </button>
                      );
                    })}
                    {canEdit ? (
                      <button
                        onClick={() => select({ kind: "section", id: s.id })}
                        title="Bölüm editörünü aç → + Soru Ekle"
                        style={{
                          marginTop: 4,
                          padding: "2px 6px",
                          fontSize: "0.7rem",
                          background: "transparent",
                          border: "1px dashed var(--color-border)",
                          borderRadius: 4,
                          color: "var(--color-primary)",
                          cursor: "pointer",
                        }}
                      >
                        + Soru (editörde)
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FormPreview(): JSX.Element {
  const tree = useFormManagerStore((s) => s.tree);
  if (!tree) return <p style={{ color: "var(--color-text-2)" }}>Yükleniyor…</p>;

  const totalQuestions = tree.sections.reduce((a, s) => a + s.questions.length, 0);
  const enabledQuestions = tree.sections.reduce(
    (a, s) => a + s.questions.filter((q) => q.enabled).length,
    0,
  );

  return (
    <div style={{ maxWidth: 820 }}>
      <div
        style={{
          marginBottom: 20,
          padding: "14px 18px",
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: 10,
          display: "flex",
          gap: 24,
          fontSize: "0.85rem",
          color: "var(--color-text-2)",
        }}
      >
        <div>
          <strong style={{ color: "var(--color-text)" }}>{tree.sections.length}</strong> bölüm
        </div>
        <div>
          <strong style={{ color: "var(--color-text)" }}>{enabledQuestions}</strong> aktif soru
        </div>
        <div>
          <strong style={{ color: "var(--color-text)" }}>
            {totalQuestions - enabledQuestions}
          </strong>{" "}
          gizli
        </div>
        <div style={{ marginLeft: "auto", color: "var(--color-text-3)", fontSize: "0.78rem" }}>
          Danışmanın göreceği halidir
        </div>
      </div>

      {tree.sections.length === 0 ? (
        <div
          style={{
            padding: "20px 24px",
            border: "1px dashed var(--color-border)",
            borderRadius: 10,
            color: "var(--color-text-3)",
          }}
        >
          Henüz bölüm yok. Editöre dönüp bölüm+soru ekleyin.
        </div>
      ) : (
        tree.sections.map((section) => (
          <section
            key={section.id}
            style={{
              marginBottom: 28,
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <header
              style={{
                padding: "14px 20px",
                background: "var(--color-bg)",
                borderBottom: "1px solid var(--color-border)",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.7rem",
                  color: "var(--color-text-3)",
                  marginBottom: 2,
                }}
              >
                {section.code}
              </div>
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.2rem",
                  color: "var(--color-text)",
                }}
              >
                {section.label}
              </h2>
              {section.description ? (
                <p style={{ color: "var(--color-text-2)", fontSize: "0.85rem", marginTop: 4 }}>
                  {section.description}
                </p>
              ) : null}
            </header>
            <div style={{ padding: "14px 20px", display: "grid", gap: 20 }}>
              {section.questions.filter((q) => q.enabled).length === 0 ? (
                <p style={{ color: "var(--color-text-3)", fontSize: "0.88rem" }}>
                  Bu bölümde aktif soru yok.
                </p>
              ) : (
                section.questions
                  .filter((q) => q.enabled)
                  .map((q) => (
                    <div
                      key={q.id}
                      style={{
                        paddingBottom: 16,
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      <QuestionPreview question={q} />
                    </div>
                  ))
              )}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function MiddlePanel({
  detailName,
  canEdit,
  isDraft,
  quarterlyProjectCount,
  onFrameworkChanged,
}: {
  detailName: string;
  canEdit: boolean;
  isDraft: boolean;
  quarterlyProjectCount: number;
  onFrameworkChanged: () => Promise<void>;
}): JSX.Element {
  const tree = useFormManagerStore((s) => s.tree);
  const selection = useFormManagerStore((s) => s.selection);
  const sel = useMemo(() => resolveSelection(tree, selection), [tree, selection]);

  if (!tree) return <p style={{ color: "var(--color-text-2)" }}>Versiyon yükleniyor…</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* M-L2: Framework Prompt her zaman editlenebilir — draft/published fark etmez */}
      <FrameworkPromptCard canEdit={canEdit} />
      {/* Raporlama dönemi (1A): çeyreklik anahtarı L2 kartının HEMEN ALTINDA.
          Anahtar framework-level'dır — L2 gibi canEdit ile canlı, isDraft gerekmez. */}
      <QuarterlyToggleRow
        canEdit={canEdit}
        quarterlyProjectCount={quarterlyProjectCount}
        onFrameworkChanged={onFrameworkChanged}
      />
      {/* 0062 — bölüm kodları standart referansı mı (DOCX "İlgili standart" dipnotu). Framework düzeyi. */}
      <StandardRefsToggleRow canEdit={canEdit} />
      <FrameworkPromptEditorBody detailName={detailName} canEdit={canEdit && isDraft} sel={sel} />
    </div>
  );
}

function FrameworkPromptCard({ canEdit }: { canEdit: boolean }): JSX.Element | null {
  const tree = useFormManagerStore((s) => s.tree);
  const patchFrameworkPrompt = useFormManagerStore((s) => s.patchFrameworkPrompt);
  // M-L2: framework-level prompt — version'dan değil tree.frameworkPrompt'tan oku.
  const initial = tree?.frameworkPrompt ?? "";
  const [open, setOpen] = useState(initial.length === 0);
  const [value, setValue] = useState(initial);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  useEffect(() => {
    if (!canEdit) return;
    if (value === initial) return;
    const t = setTimeout(() => {
      void patchFrameworkPrompt(value);
    }, 800);
    return () => clearTimeout(t);
  }, [value, initial, canEdit, patchFrameworkPrompt]);

  if (!tree) return null;
  const isEmpty = initial.trim().length === 0;

  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderLeft: `3px solid ${isEmpty ? "var(--color-warning)" : "var(--color-primary)"}`,
        borderRadius: 8,
        background: "var(--color-surface)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "10px 14px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)" }}>
            Çerçeve Prompt'u (Layer 2 — AI 4-katman)
          </span>
          {isEmpty ? (
            <span
              style={{
                fontSize: "0.65rem",
                fontWeight: 600,
                padding: "1px 6px",
                borderRadius: 4,
                background: "var(--color-warning)",
                color: "white",
              }}
            >
              BOŞ
            </span>
          ) : (
            <span style={{ fontSize: "0.7rem", color: "var(--color-text-3)" }}>
              {initial.length} karakter
            </span>
          )}
        </div>
        <span style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open ? (
        <div style={{ padding: "0 14px 14px" }}>
          {/* #002+#003+#004 — shared PromptEditor (placeholder chip + sayaç + auto-grow) */}
          <PromptEditor
            layer="framework"
            value={value}
            onChange={setValue}
            disabled={!canEdit}
            placeholder="Örn: Bu rapor {{company_name}} ({{sector}}) firmasının {{reporting_year}} GRI standartlı sürdürülebilirlik raporudur. GRI terminolojisini kullan…"
            hint="Standart-spesifik bağlam: rapor türü, terminoloji, yapı kuralları. Placeholder'ları aşağıdan tıkla → imlece yapışır."
          />
          <p style={{ fontSize: "0.72rem", color: "var(--color-text-3)", marginTop: 6 }}>
            Bu prompt tüm sürümlerde ortaktır ve canlıdır; düzenleme mevcut projeleri de bir sonraki
            üretimde etkiler.
          </p>
        </div>
      ) : null}
    </div>
  );
}

// --- Raporlama dönemi: çeyreklik anahtarı satırı (tasarım sözleşmesi 1A + 2B) ---
// Kapalıyken 36 px TEK satır (yardım metni yok); açıkken satır genişler.
// Anahtar framework-level: versiyon kilidi kullanmaz, canEdit yeter (L2 kartı gibi).
function StandardRefsToggleRow({ canEdit }: { canEdit: boolean }): JSX.Element | null {
  const tree = useFormManagerStore((s) => s.tree);
  const setValue = useFormManagerStore((s) => s.setSectionCodesAreStandardRefs);
  if (!tree) return null;
  const on = tree.sectionCodesAreStandardRefs;
  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        background: "var(--color-surface)",
        padding: "10px 14px",
        display: "grid",
        gap: 4,
      }}
    >
      <div
        title={canEdit ? undefined : FRAMEWORK_UI_COPY.standardRefsLockedTitle}
        style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
      >
        <SwitchToggle
          on={on}
          disabled={!canEdit}
          ariaLabel={FRAMEWORK_UI_COPY.standardRefsLabel}
          onChange={(v) => {
            void setValue(v);
          }}
        />
        <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)" }}>
          {FRAMEWORK_UI_COPY.standardRefsLabel}
        </span>
        {on ? null : (
          <span
            style={{
              fontSize: "0.65rem",
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: 4,
              background: "color-mix(in srgb, var(--color-text-3) 12%, white)",
              color: "var(--color-text-3)",
            }}
          >
            {FRAMEWORK_UI_COPY.standardRefsBadgeOff}
          </span>
        )}
        <span style={{ marginLeft: "auto" }}>
          <PeriodSaveIndicator />
        </span>
      </div>
      <p style={{ fontSize: "0.75rem", color: "var(--color-text-2)" }}>
        {FRAMEWORK_UI_COPY.standardRefsHelp}
      </p>
    </div>
  );
}

function QuarterlyToggleRow({
  canEdit,
  quarterlyProjectCount,
  onFrameworkChanged,
}: {
  canEdit: boolean;
  quarterlyProjectCount: number;
  onFrameworkChanged: () => Promise<void>;
}): JSX.Element | null {
  const tree = useFormManagerStore((s) => s.tree);
  const versionStatus = useFormManagerStore((s) => s.versionStatus);
  const setSupportsQuarterly = useFormManagerStore((s) => s.setSupportsQuarterly);
  const select = useFormManagerStore((s) => s.select);

  const sections = useMemo(() => tree?.sections ?? [], [tree]);
  // 2B: N server'dan değil AĞAÇTAN türetilir — çip yazımı optimistic olduğu için rozet
  // anında doğru kalsın. TEK kaynak burasıdır; DTO'da karşılık gelen sayaç YOK.
  const excluded = useMemo(() => annualExcludedQuestions(sections), [sections]);
  const noNarrowing = useMemo(() => hasNoPeriodNarrowing(sections), [sections]);

  if (!tree) return null;
  const on = tree.supportsQuarterly;

  async function onToggle(next: boolean) {
    await setSupportsQuarterly(next);
    // Sayaçlar (quarterlyProjectCount) tazelensin.
    await onFrameworkChanged();
  }

  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        background: "var(--color-surface)",
        padding: on ? "10px 14px" : "0 14px",
      }}
    >
      <div
        title={canEdit ? undefined : PERIOD_COPY.toggleLockedTitle}
        style={{
          minHeight: 36,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <SwitchToggle
          on={on}
          disabled={!canEdit}
          ariaLabel={PERIOD_COPY.toggleLabel}
          onChange={(v) => {
            void onToggle(v);
          }}
        />
        <span
          style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)" }}
          title={canEdit ? undefined : PERIOD_COPY.toggleLockedTitle}
        >
          {PERIOD_COPY.toggleLabel}
        </span>
        {on ? null : (
          <span
            style={{
              fontSize: "0.65rem",
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: 4,
              background: "color-mix(in srgb, var(--color-text-3) 12%, white)",
              color: "var(--color-text-3)",
            }}
          >
            {PERIOD_COPY.toggleBadgeOff}
          </span>
        )}
        {/* 2B — kapalıyken "görünmez kısıt" rozeti + ilk etkilenen soruya götüren link */}
        {!on && excluded.length > 0 ? (
          <span style={{ fontSize: "0.72rem", color: "var(--color-warning-text)" }}>
            {fillTemplate(PERIOD_COPY.annualExcludedBadge, { n: excluded.length })} ·{" "}
            <button
              type="button"
              onClick={() => select({ kind: "question", id: excluded[0]!.questionId })}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                color: "var(--color-primary)",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: "0.72rem",
              }}
            >
              {PERIOD_COPY.annualExcludedLink}
            </button>
          </span>
        ) : null}
        <span style={{ marginLeft: "auto" }}>
          <PeriodSaveIndicator />
        </span>
      </div>
      {on ? (
        <div style={{ display: "grid", gap: 4, marginTop: 6 }}>
          <p style={{ fontSize: "0.75rem", color: "var(--color-text-2)" }}>
            {PERIOD_COPY.toggleHelp}
          </p>
          {noNarrowing ? (
            <p style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
              {PERIOD_COPY.noNarrowingInfo}
              {versionStatus === "draft" ? "" : ` ${PERIOD_COPY.publishedHint}`}
            </p>
          ) : null}
          {quarterlyProjectCount > 0 ? (
            <p
              role="status"
              style={{
                fontSize: "0.75rem",
                color: "var(--color-warning-text)",
                background: "var(--color-warning-bg)",
                border: "1px solid var(--color-warning-border)",
                borderRadius: 6,
                padding: "4px 8px",
              }}
            >
              {fillTemplate(PERIOD_COPY.closingWarning, { n: quarterlyProjectCount })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Dönem yazımlarının satır içi kayıt göstergesi (2A). Hata satırı TIKLANIR:
// ağacı yeniden yükler (412 çakışmasında da doğru davranış).
function PeriodSaveIndicator(): JSX.Element | null {
  const saveState = useFormManagerStore((s) => s.saveState);
  const load = useFormManagerStore((s) => s.load);
  const frameworkId = useFormManagerStore((s) => s.frameworkId);
  const versionId = useFormManagerStore((s) => s.versionId);
  const [savedVisible, setSavedVisible] = useState(false);
  const prevSaveState = useRef<SaveState>(saveState);

  useEffect(() => {
    // "Kaydedildi ✓" yalnız GEÇİŞTE gösterilir (fix round 1 / bulgu 7): bileşen
    // saveState zaten "saved" iken mount olursa 2 sn tekrar oynamasın.
    const prev = prevSaveState.current;
    prevSaveState.current = saveState;
    if (saveState !== "saved") return;
    if (prev !== "saving" && prev !== "typing") return;
    setSavedVisible(true);
    const t = setTimeout(() => setSavedVisible(false), 2000);
    return () => clearTimeout(t);
  }, [saveState]);

  if (saveState === "saving") {
    return (
      <span role="status" style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>
        {PERIOD_COPY.saveSaving}
      </span>
    );
  }
  if (saveState === "error" || saveState === "conflict") {
    return (
      <button
        type="button"
        onClick={() => {
          if (frameworkId && versionId) void load(frameworkId, versionId);
        }}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          fontSize: "0.72rem",
          color: "var(--color-error)",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {PERIOD_COPY.saveError}
      </button>
    );
  }
  if (saveState === "saved" && savedVisible) {
    return (
      <span role="status" style={{ fontSize: "0.72rem", color: "var(--color-success)" }}>
        {PERIOD_COPY.saveSaved}
      </span>
    );
  }
  return null;
}

function FrameworkPromptEditorBody({
  detailName,
  canEdit,
  sel,
}: {
  detailName: string;
  canEdit: boolean;
  sel: ReturnType<typeof resolveSelection>;
}): JSX.Element {
  if (sel.kind === "none") {
    return (
      <div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.6rem",
            color: "var(--color-text)",
          }}
        >
          {detailName}
        </h1>
        <p style={{ color: "var(--color-text-2)", marginTop: 8 }}>
          Sol panelden bölüm veya soru seçin.
        </p>
        {!canEdit ? (
          <div
            style={{
              marginTop: 16,
              padding: "10px 14px",
              background: "color-mix(in srgb, var(--color-warning) 15%, white)",
              borderRadius: 6,
              fontSize: "0.85rem",
              color: "var(--color-text-2)",
            }}
          >
            Bu versiyon düzenlenemez (draft değil). Yeni bir versiyon fork edin.
          </div>
        ) : null}
      </div>
    );
  }

  if (sel.kind === "section") {
    return <SectionEditor section={sel.section} canEdit={canEdit} />;
  }

  // Dönem sonuç satırı bölüm tavanını da gösterir → bölümü de geçiyoruz.
  return <QuestionEditor question={sel.question} section={sel.section} canEdit={canEdit} />;
}

function SectionEditor({
  section,
  canEdit,
}: {
  section: SectionWithQuestions;
  canEdit: boolean;
}): JSX.Element {
  const patchSection = useFormManagerStore((s) => s.patchSection);
  const removeSection = useFormManagerStore((s) => s.removeSection);
  // Raporlama dönemi (1B/2A): çipler + mappingHints. `canEdit` burada zaten
  // canEdit && isDraft; kilit SATIRINI yalnız yayındaki sürümde gösteririz.
  const setSectionPeriods = useFormManagerStore((s) => s.setSectionPeriods);
  const setMappingHints = useFormManagerStore((s) => s.setMappingHints);
  const supportsQuarterly = useFormManagerStore((s) => s.tree?.supportsQuarterly ?? false);
  const isDraft = useFormManagerStore((s) => s.versionStatus === "draft");
  // R7: satır `!canEdit || !isDraft` iken görünür; yayındaki sürüm ile yetkisizlik
  // ayrı cümlelerdir (prop zaten canEdit && isDraft olduğu için sıralama önemli).
  const lockedNote = !isDraft
    ? PERIOD_COPY.lockedLine
    : canEdit
      ? null
      : PERIOD_COPY.lockedNoPermission;
  const [code, setCode] = useState(section.code);
  const [label, setLabel] = useState(section.label);
  const [description, setDescription] = useState(section.description ?? "");
  const [sectionPrompt, setSectionPrompt] = useState(section.sectionPrompt);

  useEffect(() => {
    setCode(section.code);
    setLabel(section.label);
    setDescription(section.description ?? "");
    setSectionPrompt(section.sectionPrompt);
  }, [section.id]);

  // Debounce auto-save
  useEffect(() => {
    if (!canEdit) return;
    if (
      code === section.code &&
      label === section.label &&
      description === (section.description ?? "") &&
      sectionPrompt === section.sectionPrompt
    )
      return;
    const t = setTimeout(() => {
      void patchSection(section.id, {
        code,
        label,
        description: description || null,
        sectionPrompt,
      });
    }, 800);
    return () => clearTimeout(t);
  }, [code, label, description, sectionPrompt]);

  async function onDelete() {
    if (
      !confirm(
        `"${section.label}" bölümünü ve ${section.questions.length} sorusunu silmek istediğinize emin misiniz?`,
      )
    )
      return;
    await removeSection(section.id);
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.5rem",
            color: "var(--color-text)",
          }}
        >
          Bölüm Düzenle
        </h1>
        {/* 2A — QuestionEditor ile aynı satır içi kayıt göstergesi */}
        <span style={{ marginLeft: "auto", marginRight: 12 }}>
          <PeriodSaveIndicator />
        </span>
        {canEdit ? (
          <button
            onClick={() => void onDelete()}
            style={{
              padding: "6px 12px",
              fontSize: "0.8rem",
              background: "transparent",
              border: "1px solid var(--color-error)",
              borderRadius: 6,
              color: "var(--color-error)",
              cursor: "pointer",
            }}
          >
            Bölümü Sil
          </button>
        ) : null}
      </div>

      <div style={{ display: "grid", gap: 14, maxWidth: 720 }}>
        <FieldRow label="Bölüm Kodu">
          <input
            type="text"
            value={code}
            disabled={!canEdit}
            onChange={(e) => setCode(e.target.value)}
            style={fieldInputStyle}
          />
        </FieldRow>
        <FieldRow label="Bölüm Adı">
          <input
            type="text"
            value={label}
            disabled={!canEdit}
            onChange={(e) => setLabel(e.target.value)}
            style={fieldInputStyle}
          />
        </FieldRow>
        <FieldRow label="Açıklama">
          <textarea
            value={description}
            rows={2}
            disabled={!canEdit}
            onChange={(e) => setDescription(e.target.value)}
            style={fieldInputStyle}
          />
        </FieldRow>
        {/* Dönem çipleri — bölüm TAVANI (1B). Anahtar kapalıyken DOM'da hiç yok (2B). */}
        {supportsQuarterly ? (
          <PeriodChips
            legend={PERIOD_COPY.sectionLegend}
            help={PERIOD_COPY.sectionHelp}
            value={section.applicablePeriods}
            disabled={!canEdit}
            lockedNote={lockedNote}
            onChange={(next) => setSectionPeriods(section.id, next)}
          />
        ) : null}
        {/* mappingHints — çiplerin ALTINDA, ikincil (7A). Ekleme/silme anında,
            OPTIMISTIC store yazıcısı üzerinden (ardışık silmeler kaybolmasın). */}
        <MappingHintsInput
          value={section.mappingHints}
          disabled={!canEdit}
          onChange={(next) => setMappingHints(section.id, next)}
        />
        <FieldRow label="Bölüm Prompt (Layer 3 — AI 4-katman)">
          {/* #002+#003+#004 — shared PromptEditor (Layer 3 scope chip'leri + sayaç) */}
          <PromptEditor
            layer="section"
            value={sectionPrompt}
            onChange={setSectionPrompt}
            disabled={!canEdit}
            placeholder="Bu bölüm AI tarafından rapor metnine çevrilirken dikkat edilecek yönergeler…"
            hint="Bölüm-spesifik bağlam: yapı (1./2./3.), uzunluk hedefi, ton. Bölüm + çerçeve placeholder'ları kullanılabilir."
          />
        </FieldRow>
      </div>

      <QuestionsPanel section={section} canEdit={canEdit} />
    </div>
  );
}

function QuestionsPanel({
  section,
  canEdit,
}: {
  section: SectionWithQuestions;
  canEdit: boolean;
}): JSX.Element {
  const addQuestion = useFormManagerStore((s) => s.addQuestion);
  const select = useFormManagerStore((s) => s.select);
  const removeQuestion = useFormManagerStore((s) => s.removeQuestion);
  const moveQuestion = useFormManagerStore((s) => s.moveQuestion);
  const reorderQuestions = useFormManagerStore((s) => s.reorderQuestions);

  const [addOpen, setAddOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<QuestionType>("text");
  const [dragQuestionId, setDragQuestionId] = useState<string | null>(null);

  function resetForm() {
    setNewCode("");
    setNewLabel("");
    setNewType("text");
  }

  // In-flight kilidi (Finar çift-tık bug'ı, 2026-09-09 staging): "Ekle" bir kez
  // tıklanınca istek dönene kadar ikinci tık yok sayılır. Staging'de tek tıkla
  // 1,5 ms içinde 4 POST gitmişti (donanım çift tetikleme) → 3×412 → sahte modal.
  const addingRef = useRef(false);
  const [adding, setAdding] = useState(false);

  async function onAdd() {
    if (!newCode.trim() || !newLabel.trim()) return;
    if (addingRef.current) return;
    addingRef.current = true;
    setAdding(true);
    try {
      await addQuestion(section.id, newCode.trim(), newLabel.trim(), newType);
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
    resetForm();
    setAddOpen(false);
  }

  async function onDelete(q: Question) {
    if (!confirm(`"${q.label}" sorusunu silmek istediğinize emin misiniz?`)) return;
    await removeQuestion(q.id);
  }

  return (
    <div style={{ marginTop: 32, maxWidth: 820 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 10,
          paddingTop: 20,
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.15rem",
            color: "var(--color-text)",
          }}
        >
          Sorular{" "}
          <span style={{ color: "var(--color-text-3)", fontSize: "0.85rem", fontWeight: 400 }}>
            ({section.questions.length})
          </span>
        </h2>
        {canEdit ? (
          <button
            onClick={() => setAddOpen((s) => !s)}
            className="px-3 py-1.5 text-sm rounded"
            style={{
              background: addOpen ? "transparent" : "var(--color-primary)",
              color: addOpen ? "var(--color-text-2)" : "var(--color-surface)",
              border: addOpen ? "1px solid var(--color-border)" : "none",
            }}
          >
            {addOpen ? "İptal" : "+ Soru Ekle"}
          </button>
        ) : null}
      </div>

      {addOpen ? (
        <div
          style={{
            padding: 14,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            marginBottom: 12,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 180px", gap: 10 }}>
            <input
              type="text"
              placeholder="Soru kodu (ör. Q-302-1)"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              style={fieldInputStyle}
            />
            <input
              type="text"
              placeholder="Soru etiketi"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              style={fieldInputStyle}
            />
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value as QuestionType)}
              style={fieldInputStyle}
            >
              {QUESTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => {
                resetForm();
                setAddOpen(false);
              }}
              className="px-3 py-1.5 text-sm rounded"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text)",
              }}
            >
              İptal
            </button>
            <button
              onClick={() => void onAdd()}
              disabled={adding || !newCode.trim() || !newLabel.trim()}
              className="px-3 py-1.5 text-sm rounded font-semibold disabled:opacity-50"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              {adding ? "Ekleniyor…" : "Ekle"}
            </button>
          </div>
        </div>
      ) : null}

      {section.questions.length === 0 ? (
        <div
          style={{
            padding: "14px 18px",
            border: "1px dashed var(--color-border)",
            borderRadius: 8,
            fontSize: "0.88rem",
            color: "var(--color-text-3)",
          }}
        >
          Bu bölümde henüz soru yok. {canEdit ? '"+ Soru Ekle" ile başlayın.' : ""}
        </div>
      ) : (
        <div
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            overflow: "hidden",
            background: "var(--color-surface)",
          }}
        >
          {section.questions.map((q, i) => (
            <div
              key={q.id}
              draggable={canEdit}
              onDragStart={(e) => {
                setDragQuestionId(q.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => {
                if (dragQuestionId && dragQuestionId !== q.id) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (!dragQuestionId || dragQuestionId === q.id) return;
                const ids = section.questions.map((x) => x.id);
                const from = ids.indexOf(dragQuestionId);
                const to = ids.indexOf(q.id);
                if (from < 0 || to < 0) return;
                const next = [...ids];
                next.splice(from, 1);
                next.splice(to, 0, dragQuestionId);
                setDragQuestionId(null);
                void reorderQuestions(section.id, next);
              }}
              onDragEnd={() => setDragQuestionId(null)}
              style={{
                display: "grid",
                gridTemplateColumns: canEdit ? "50px 110px 1fr 90px auto" : "110px 1fr 90px auto",
                gap: 10,
                padding: "10px 14px",
                alignItems: "center",
                borderTop: i === 0 ? "none" : "1px solid var(--color-border)",
                opacity: dragQuestionId === q.id ? 0.5 : q.enabled ? 1 : 0.55,
                cursor: canEdit ? "grab" : "default",
              }}
            >
              {canEdit ? (
                <div style={{ display: "flex", gap: 2, flexDirection: "column" }}>
                  <button
                    onClick={() => void moveQuestion(q.id, "up")}
                    disabled={i === 0}
                    title="Yukarı taşı"
                    style={sectionMoveBtnStyle(i === 0)}
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => void moveQuestion(q.id, "down")}
                    disabled={i === section.questions.length - 1}
                    title="Aşağı taşı"
                    style={sectionMoveBtnStyle(i === section.questions.length - 1)}
                  >
                    ▼
                  </button>
                </div>
              ) : null}
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.75rem",
                  color: "var(--color-text-3)",
                }}
              >
                {q.code}
              </span>
              <button
                onClick={() => select({ kind: "question", id: q.id })}
                style={{
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  padding: 0,
                }}
              >
                {q.label}
                {q.required ? (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: "0.65rem",
                      color: "var(--color-error)",
                      fontWeight: 600,
                    }}
                  >
                    ZORUNLU
                  </span>
                ) : null}
              </button>
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "var(--color-text-3)",
                  background: "var(--color-bg)",
                  padding: "2px 8px",
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  justifyContent: "center",
                }}
              >
                <QuestionTypeIcon type={q.type} size={12} />
                {TYPE_LABELS[q.type]}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => select({ kind: "question", id: q.id })}
                  className="px-2.5 py-1 text-xs rounded"
                  style={{
                    background: "transparent",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-primary)",
                  }}
                >
                  Düzenle
                </button>
                {canEdit ? (
                  <button
                    onClick={() => void onDelete(q)}
                    className="px-2.5 py-1 text-xs rounded"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--color-border)",
                      color: "var(--color-error)",
                    }}
                  >
                    Sil
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionEditor({
  question,
  section,
  canEdit,
}: {
  question: Question;
  section: SectionWithQuestions;
  canEdit: boolean;
}): JSX.Element {
  const patchQuestion = useFormManagerStore((s) => s.patchQuestion);
  const changeType = useFormManagerStore((s) => s.changeQuestionType);
  const removeQuestion = useFormManagerStore((s) => s.removeQuestion);
  // Raporlama dönemi (1B/2B)
  const setQuestionPeriods = useFormManagerStore((s) => s.setQuestionPeriods);
  const select = useFormManagerStore((s) => s.select);
  const supportsQuarterly = useFormManagerStore((s) => s.tree?.supportsQuarterly ?? false);
  const isDraft = useFormManagerStore((s) => s.versionStatus === "draft");
  // R7 — bkz. SectionEditor.
  const lockedNote = !isDraft
    ? PERIOD_COPY.lockedLine
    : canEdit
      ? null
      : PERIOD_COPY.lockedNoPermission;

  const [local, setLocal] = useState<QuestionPatch>(toLocal(question));
  // Debounce timeout ref — "Kaydet" manuel flush için, "Sıfırla" iptal için gerekli.
  const timeoutRef = useRef<number | null>(null);
  // Tip değişiminde de re-sync — changeType server'da options'ı default'a çevirir,
  // local de buna hizalanmalı (yoksa stale numeric options file tipine auto-save
  // olmaya çalışır → 422).
  useEffect(() => {
    setLocal(toLocal(question));
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [question.id, question.type]);

  // Çipler store'a ANINDA yazar (trailing-merge); ağaç optimistic güncellenince
  // local'i de hizala — aksi halde bayat local 800 ms sonra eski listeyi geri yazar.
  const questionPeriodsKey = JSON.stringify(question.applicablePeriods);
  useEffect(() => {
    setLocal((l) => ({ ...l, applicablePeriods: [...question.applicablePeriods] }));
  }, [questionPeriodsKey]);

  const diff = useMemo(() => diffPatch(question, local), [question, local]);
  const hasChanges = Object.keys(diff).length > 0;

  // Auto-save debounce — 800ms sonrası PATCH gönderir.
  useEffect(() => {
    if (!canEdit) return;
    if (!hasChanges) return;
    const t = window.setTimeout(() => {
      void patchQuestion(question.id, diff);
      timeoutRef.current = null;
    }, 800);
    timeoutRef.current = t;
    return () => {
      window.clearTimeout(t);
      if (timeoutRef.current === t) timeoutRef.current = null;
    };
  }, [local]);

  function onReset() {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setLocal(toLocal(question));
  }

  function onManualSave() {
    if (!hasChanges) return;
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    void patchQuestion(question.id, diff);
  }

  async function onTypeChange(newType: QuestionType) {
    if (newType === question.type) return;
    if (
      !confirm(
        `Tipi "${question.type}" → "${newType}" olarak değiştirmek, seçenek/ayarları sıfırlayacak. Devam edilsin mi?`,
      )
    )
      return;
    await changeType(question.id, newType);
  }

  async function onDelete() {
    if (!confirm(`"${question.label}" sorusunu silmek istediğinize emin misiniz?`)) return;
    await removeQuestion(question.id);
  }

  // Davranış toggle'ları — Ek A matrisine göre tip-özel filtre.
  // `enabled` header'daki switch'e taşındı, listeden çıkarıldı (tek yer kuralı).
  // M15 D8 — composite'te "Zorunlu" toggle'ı GİZLİ: zorunluluk parça bazında tanımlanır,
  // soru-düzeyi bayrak backend'de parts.some(required) ile TÜRETİLİR (D13).
  const behaviorToggles: { k: keyof QuestionPatch; label: string }[] = [];
  if (question.type !== "composite") {
    behaviorToggles.push({ k: "required", label: "Zorunlu" });
  }
  // Opt-out semantiği: öneri default görünür; toggle "bu soruda gizle" istisnası.
  // Eski Ek A tip filtresi kaldırıldı — gizleme her tipte anlamlı (file'da value
  // zaten boş kaldığından öneri kendiliğinden çıkmaz).
  behaviorToggles.push({ k: "priorYearHintDisabled", label: "Önceki yıl önerisini gizle" });
  // allowEvidence: file'da gizle (dosya sorusu zaten dosya alıyor).
  // M15 D15 — composite'te de gizle: dosya parçası varsa şema zaten reddediyor,
  // parçasızda bile aynı attachment havuzu karışıklığı yaratır; UI hiç göstermez.
  if (question.type !== "file" && question.type !== "composite") {
    behaviorToggles.push({ k: "allowEvidence", label: "Kanıt belgesi" });
  }
  behaviorToggles.push({ k: "allowNote", label: "Danışman notu" });

  // Tavan + kesişim (1B): bölüm TAVAN, soru TERCİH, etkin = kesişim.
  const effective = effectivePeriods(section.applicablePeriods, question.applicablePeriods);
  const periodResult = describeEffectivePeriods(effective);
  const outsideNote = formatOutsideNote(
    outsideCeiling(section.applicablePeriods, question.applicablePeriods),
  );
  const periodResultId = `period-result-${question.id}`;
  // "Bölüm {code} kapsamı:" — kod link olduğu için şablon ikiye bölünür (tek kopya kaynağı).
  const [scopeBefore, scopeAfter] = PERIOD_COPY.sectionScope.split("{code}");

  const lastSavedAt = useMemo(() => {
    try {
      const d = new Date(question.updatedAt);
      return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return null;
    }
  }, [question.updatedAt]);

  return (
    <div
      style={{
        maxWidth: 820,
        display: "flex",
        flexDirection: "column",
        minHeight: "calc(100vh - 180px)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.75rem",
                color: "var(--color-text-3)",
                padding: "2px 6px",
                background: "var(--color-surface)",
                borderRadius: 4,
              }}
            >
              {question.code}
            </span>
            {local.required ? (
              <span
                style={{
                  fontSize: "0.65rem",
                  color: "var(--color-error)",
                  fontWeight: 600,
                }}
              >
                ZORUNLU
              </span>
            ) : null}
            {!local.enabled ? (
              <span
                style={{
                  fontSize: "0.65rem",
                  color: "var(--color-text-3)",
                  fontWeight: 600,
                }}
              >
                GİZLİ
              </span>
            ) : null}
          </div>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "1.4rem",
              color: "var(--color-text)",
            }}
          >
            Soru Düzenle
          </h1>
        </div>
        {/* 2A — SectionEditor ile aynı satır içi kayıt göstergesi (dönem çipleri) */}
        <PeriodSaveIndicator />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: "0.85rem",
              color: "var(--color-text-2)",
              cursor: canEdit ? "pointer" : "default",
              userSelect: "none",
            }}
          >
            <span>Bu soruyu göster</span>
            <SwitchToggle
              on={local.enabled}
              disabled={!canEdit}
              onChange={(v) => setLocal({ ...local, enabled: v })}
            />
          </label>
          {canEdit ? (
            <button
              onClick={() => void onDelete()}
              style={{
                padding: "6px 12px",
                fontSize: "0.8rem",
                background: "transparent",
                border: "1px solid var(--color-error)",
                borderRadius: 6,
                color: "var(--color-error)",
                cursor: "pointer",
              }}
            >
              Soruyu Sil
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        <FieldRow label="Soru Kodu">
          <input
            type="text"
            value={local.code}
            disabled={!canEdit}
            onChange={(e) => setLocal({ ...local, code: e.target.value })}
            style={fieldInputStyle}
          />
        </FieldRow>
        <FieldRow label="Soru Etiketi">
          <input
            type="text"
            value={local.label}
            disabled={!canEdit}
            onChange={(e) => setLocal({ ...local, label: e.target.value })}
            style={fieldInputStyle}
          />
        </FieldRow>
        <FieldRow label="Açıklama">
          <textarea
            value={local.description ?? ""}
            rows={2}
            disabled={!canEdit}
            onChange={(e) => setLocal({ ...local, description: e.target.value || null })}
            style={fieldInputStyle}
          />
        </FieldRow>

        <FieldRow label="Soru Tipi">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {QUESTION_TYPES.filter((t) => t !== "composite").map((t) => {
              const active = t === question.type;
              return (
                <button
                  key={t}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => void onTypeChange(t)}
                  style={{
                    padding: "10px 12px",
                    border: active
                      ? "2px solid var(--color-primary)"
                      : "1px solid var(--color-border)",
                    borderRadius: 6,
                    background: active ? "rgba(15,42,61,0.04)" : "var(--color-bg)",
                    color: active ? "var(--color-primary)" : "var(--color-text)",
                    fontWeight: active ? 600 : 400,
                    fontSize: "0.85rem",
                    cursor: canEdit ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    justifyContent: "flex-start",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      width: 24,
                      height: 24,
                      alignItems: "center",
                      justifyContent: "center",
                      color: active ? "var(--color-primary)" : "var(--color-text-2)",
                    }}
                  >
                    <QuestionTypeIcon type={t} size={20} />
                  </span>
                  <span>{TYPE_LABELS[t]}</span>
                </button>
              );
            })}
          </div>
          {/* M15 U3 — composite 9'un eşi değil, onları KAPSAYAN yapı: ayrı tam-genişlik kart */}
          {(() => {
            const active = question.type === "composite";
            return (
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => void onTypeChange("composite")}
                style={{
                  marginTop: 8,
                  width: "100%",
                  padding: "10px 12px",
                  border: active
                    ? "2px solid var(--color-primary)"
                    : "1px solid var(--color-border)",
                  borderRadius: 6,
                  background: active ? "rgba(15,42,61,0.04)" : "var(--color-bg)",
                  color: active ? "var(--color-primary)" : "var(--color-text)",
                  fontWeight: active ? 600 : 400,
                  fontSize: "0.85rem",
                  cursor: canEdit ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "flex-start",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    width: 24,
                    height: 24,
                    alignItems: "center",
                    justifyContent: "center",
                    color: active ? "var(--color-primary)" : "var(--color-text-2)",
                    flexShrink: 0,
                  }}
                >
                  <QuestionTypeIcon type="composite" size={20} />
                </span>
                <span>
                  <span style={{ display: "block" }}>{TYPE_LABELS.composite}</span>
                  <span
                    style={{
                      display: "block",
                      fontSize: "0.72rem",
                      fontWeight: 400,
                      color: "var(--color-text-2)",
                    }}
                  >
                    Birden fazla giriş alanını tek soruda birleştirir
                  </span>
                </span>
              </button>
            );
          })()}
        </FieldRow>

        <FieldRow label={`${TYPE_LABELS[question.type]} — Ayarlar`}>
          {/* M15 self-review B3 — key: soru değişince editör alt-ağacı remount olsun;
              ChoiceListEditor'ün autoLinked indeks ref'i önceki sorunun oturum durumunu
              taşıyıp mevcut seçenek ID'lerini sessizce yeniden yazabiliyordu */}
          <QuestionTypeEditor
            key={question.id}
            options={local.options}
            onChange={(o) => setLocal({ ...local, options: o })}
            disabled={!canEdit}
          />
        </FieldRow>

        <FieldRow label="Yardımcı Not">
          <input
            type="text"
            value={local.helperText ?? ""}
            disabled={!canEdit}
            placeholder="Soru altında gösterilecek yardımcı metin"
            onChange={(e) => setLocal({ ...local, helperText: e.target.value || null })}
            style={fieldInputStyle}
          />
        </FieldRow>

        {/* Davranış toggle'ları — Ek A tip-özel enforcement */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            padding: 14,
            background: "var(--color-surface)",
            borderRadius: 8,
            border: "1px solid var(--color-border)",
          }}
        >
          {behaviorToggles.map((t) => (
            <label
              key={t.k}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: "0.85rem",
                cursor: canEdit ? "pointer" : "not-allowed",
              }}
            >
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={Boolean(local[t.k])}
                onChange={(e) => setLocal({ ...local, [t.k]: e.target.checked })}
              />
              {t.label}
            </label>
          ))}
        </div>

        {/* Dönem çipleri + KALICI sonuç satırı (1B). Anahtar kapalıyken çip/sonuç
            satırı DOM'da YOK; yerine salt-okunur "yıllıkta sorulmaz" uyarısı (2B). */}
        {supportsQuarterly ? (
          <div>
            <PeriodChips
              legend={PERIOD_COPY.questionLegend}
              help={PERIOD_COPY.questionHelp}
              value={question.applicablePeriods}
              ceiling={section.applicablePeriods}
              disabled={!canEdit}
              lockedNote={lockedNote}
              describedById={periodResultId}
              onChange={(next) => setQuestionPeriods(question.id, next)}
            />
            <p
              id={periodResultId}
              role={periodResult.tone === "warning" ? "status" : undefined}
              style={{
                marginTop: 6,
                fontSize: "0.75rem",
                color:
                  periodResult.tone === "warning"
                    ? "var(--color-warning-text)"
                    : "var(--color-text-2)",
              }}
            >
              {periodResult.text}
              {" · "}
              {scopeBefore}
              <button
                type="button"
                onClick={() => select({ kind: "section", id: section.id })}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  color: "var(--color-primary)",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                }}
              >
                {section.code}
              </button>
              {scopeAfter} {formatPeriodBadge(section.applicablePeriods)}
              {outsideNote ? ` · ${outsideNote}` : ""}
            </p>
          </div>
        ) : effective.includes("annual") ? null : (
          <p role="status" style={{ fontSize: "0.75rem", color: "var(--color-warning-text)" }}>
            {fillTemplate(PERIOD_COPY.questionAnnualExcluded, {
              periods: formatPeriodBadge(effective),
            })}
          </p>
        )}

        {/* AI Context — gradient card */}
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            background: "linear-gradient(135deg, rgba(15,42,61,0.05), rgba(240,199,94,0.08))",
            border: "1px solid var(--color-border)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 6,
            }}
          >
            <strong style={{ color: "var(--color-text)", fontSize: "0.95rem" }}>
              AI Bağlam Yönergesi (Layer 4 — AI 4-katman)
            </strong>
            <span style={{ fontSize: "0.7rem", color: "var(--color-text-3)" }}>
              soruya özel stil ipucu
            </span>
          </div>
          {/* #002+#003+#004 — shared PromptEditor (Layer 4 question scope chip'leri) */}
          <PromptEditor
            layer="question"
            value={local.aiContext ?? ""}
            onChange={(v) => setLocal({ ...local, aiContext: v || null })}
            disabled={!canEdit}
            rows={3}
            placeholder="Bu soru AI tarafından rapor metnine çevrilirken yönergeler. Boş bırakılırsa varsayılan kullanılır."
            hint="Soru-spesifik stil ipucu. Soru + bölüm + çerçeve placeholder'larına ek olarak {{previous_year_answer}} kullanılabilir."
          />
        </div>
      </div>

      {/* FOOTER — §5.4: "Otomatik kaydedildi · HH:MM" + Sıfırla + Kaydet */}
      {canEdit ? (
        <div
          style={{
            marginTop: "auto",
            paddingTop: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            fontSize: "0.8rem",
            color: "var(--color-text-3)",
            borderTop: "1px solid var(--color-border)",
            paddingBottom: 4,
          }}
        >
          <div>
            {hasChanges ? (
              <span style={{ color: "var(--color-text-2)" }}>⏳ Kaydedilmemiş değişiklikler…</span>
            ) : lastSavedAt ? (
              <span>Otomatik kaydedildi · {lastSavedAt}</span>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onReset}
              disabled={!hasChanges}
              style={{
                padding: "6px 14px",
                fontSize: "0.82rem",
                background: "transparent",
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                color: hasChanges ? "var(--color-text)" : "var(--color-text-3)",
                cursor: hasChanges ? "pointer" : "not-allowed",
                opacity: hasChanges ? 1 : 0.6,
              }}
            >
              Sıfırla
            </button>
            <button
              type="button"
              onClick={onManualSave}
              disabled={!hasChanges}
              style={{
                padding: "6px 16px",
                fontSize: "0.82rem",
                background: hasChanges ? "var(--color-primary)" : "var(--color-bg)",
                border: hasChanges ? "none" : "1px solid var(--color-border)",
                borderRadius: 6,
                color: hasChanges ? "var(--color-surface)" : "var(--color-text-3)",
                cursor: hasChanges ? "pointer" : "not-allowed",
                fontWeight: 600,
                opacity: hasChanges ? 1 : 0.6,
              }}
            >
              Kaydet
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Switch toggle — header "Bu soruyu göster" için design ref benzeri
function SwitchToggle({
  on,
  disabled,
  onChange,
  ariaLabel,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  // Etiketi <label> sarmalamayan kullanımlar için erişilebilir ad (fix round 1 / bulgu 11).
  ariaLabel?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 36,
        height: 20,
        padding: 2,
        borderRadius: 999,
        border: "none",
        background: on ? "var(--color-primary)" : "var(--color-border)",
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 120ms ease",
        opacity: disabled ? 0.55 : 1,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          display: "block",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "var(--color-surface)",
          transform: on ? "translateX(16px)" : "translateX(0)",
          transition: "transform 120ms ease",
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        }}
      />
    </button>
  );
}

/** run2 D2 — yayın sonrası sektör paketi bandının kendi kendine kaybolma süresi (ms). */
const DERIVED_BAND_MS = 20_000;

/**
 * M9 (parti 06cc §9 UI) — çerçevede TEK `SEK-*` bloğu varken türetilen paket hiçbir
 * bölümü hariç tutmaz; kutu metni eskiden bunu söylemiyor ("GYO paketi" diye kesiliyor)
 * ve danışman neyin değiştiğini anlamıyordu.
 */
const SECTOR_DERIVE_NO_EXCLUSION = "hariç tutulan bölüm yok, paket çerçevenin tamamını kapsar";

const TYPE_LABELS: Record<QuestionType, string> = {
  text: "Serbest Metin",
  numeric: "Sayısal",
  single: "Tek Seçim",
  multi: "Çoklu Seçim",
  date: "Tarih",
  file: "Dosya",
  table: "Tablo",
  percent: "Yüzde",
  repeater: "Tekrarlanabilir",
  composite: "Bileşik",
};

function RightPanel({
  onClose,
  devMode = false,
}: {
  onClose?: () => void;
  devMode?: boolean;
}): JSX.Element {
  const tree = useFormManagerStore((s) => s.tree);
  const selection = useFormManagerStore((s) => s.selection);
  const sel = useMemo(() => resolveSelection(tree, selection), [tree, selection]);
  const [tab, setTab] = useState<"preview" | "json">("preview");
  const [viewport, setViewport] = useState<"desktop" | "tablet">("desktop");

  // Dev mode kapalıysa JSON tab'ı gizle + yanlışlıkla seçiliyse preview'a döndür
  useEffect(() => {
    if (!devMode && tab === "json") setTab("preview");
  }, [devMode, tab]);

  const previewContent =
    sel.kind === "question" ? (
      <QuestionPreview question={sel.question} />
    ) : sel.kind === "section" ? (
      <SectionPreviewCard section={sel.section} />
    ) : (
      <p style={{ color: "var(--color-text-3)", fontSize: "0.85rem" }}>
        Bir bölüm veya soru seçin.
      </p>
    );

  return (
    <>
      <div style={{ padding: "14px", borderBottom: "1px solid var(--color-border)" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {onClose ? (
            <button
              onClick={onClose}
              className="form-manager-drawer-toggle"
              style={{
                padding: "4px 8px",
                fontSize: "0.75rem",
                border: "1px solid var(--color-border)",
                borderRadius: 4,
                background: "transparent",
                cursor: "pointer",
                marginRight: 6,
              }}
            >
              ←
            </button>
          ) : null}
          <button
            onClick={() => setTab("preview")}
            style={{
              flex: 1,
              padding: "6px 10px",
              fontSize: "0.85rem",
              background: tab === "preview" ? "var(--color-primary)" : "transparent",
              color: tab === "preview" ? "var(--color-surface)" : "var(--color-text-2)",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Önizleme
          </button>
          {devMode && (
            <button
              onClick={() => setTab("json")}
              title="Dev Mode — Sistem Ayarları → Arayüz'den açılır"
              style={{
                flex: 1,
                padding: "6px 10px",
                fontSize: "0.85rem",
                background: tab === "json" ? "var(--color-primary)" : "transparent",
                color: tab === "json" ? "var(--color-surface)" : "var(--color-text-2)",
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              JSON
            </button>
          )}
        </div>

        {/* Viewport sub-tab — sadece preview modda, §5.5 üst kart */}
        {tab === "preview" ? (
          <div
            role="tablist"
            aria-label="Önizleme viewport"
            style={{ display: "flex", gap: 4, marginTop: 10 }}
          >
            <button
              role="tab"
              aria-selected={viewport === "desktop"}
              onClick={() => setViewport("desktop")}
              style={{
                flex: 1,
                padding: "4px 8px",
                fontSize: "0.72rem",
                background: viewport === "desktop" ? "var(--color-bg)" : "transparent",
                border: "1px solid var(--color-border)",
                borderRadius: 4,
                color: viewport === "desktop" ? "var(--color-primary)" : "var(--color-text-3)",
                fontWeight: viewport === "desktop" ? 600 : 400,
                cursor: "pointer",
              }}
            >
              🖥 Desktop
            </button>
            <button
              role="tab"
              aria-selected={viewport === "tablet"}
              onClick={() => setViewport("tablet")}
              style={{
                flex: 1,
                padding: "4px 8px",
                fontSize: "0.72rem",
                background: viewport === "tablet" ? "var(--color-bg)" : "transparent",
                border: "1px solid var(--color-border)",
                borderRadius: 4,
                color: viewport === "tablet" ? "var(--color-primary)" : "var(--color-text-3)",
                fontWeight: viewport === "tablet" ? 600 : 400,
                cursor: "pointer",
              }}
            >
              📱 Tablet
            </button>
          </div>
        ) : null}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
        {tab === "preview" ? (
          <div
            style={{
              maxWidth: viewport === "tablet" ? 420 : "100%",
              margin: viewport === "tablet" ? "0 auto" : undefined,
              padding: viewport === "tablet" ? 12 : 0,
              border: viewport === "tablet" ? "1px dashed var(--color-border)" : "none",
              borderRadius: viewport === "tablet" ? 10 : 0,
              background: viewport === "tablet" ? "var(--color-bg)" : "transparent",
              transition: "max-width 180ms ease",
            }}
          >
            {previewContent}
          </div>
        ) : sel.kind === "question" ? (
          <JsonView obj={sel.question} />
        ) : sel.kind === "section" ? (
          <JsonView obj={sel.section} />
        ) : (
          <p style={{ color: "var(--color-text-3)", fontSize: "0.85rem" }}>
            Bir bölüm veya soru seçin.
          </p>
        )}
      </div>
    </>
  );
}

function SectionPreviewCard({ section }: { section: SectionWithQuestions }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: "0.7rem", color: "var(--color-text-3)", marginBottom: 4 }}>
        {section.code}
      </div>
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "1.1rem",
          color: "var(--color-text)",
          marginBottom: 4,
        }}
      >
        {section.label}
      </h3>
      {section.description ? (
        <p style={{ color: "var(--color-text-2)", fontSize: "0.85rem", marginBottom: 8 }}>
          {section.description}
        </p>
      ) : null}
      <div style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
        {section.questions.length} soru
      </div>
    </div>
  );
}

function JsonView({ obj }: { obj: unknown }): JSX.Element {
  const txt = JSON.stringify(obj, null, 2);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        <button
          onClick={() => void navigator.clipboard.writeText(txt)}
          style={{
            padding: "3px 8px",
            fontSize: "0.7rem",
            border: "1px solid var(--color-border)",
            borderRadius: 4,
            background: "transparent",
            color: "var(--color-text-2)",
            cursor: "pointer",
          }}
        >
          Kopyala
        </button>
      </div>
      <pre
        style={{
          fontSize: "0.72rem",
          fontFamily: "var(--font-mono)",
          background: "var(--color-bg)",
          padding: 10,
          borderRadius: 6,
          border: "1px solid var(--color-border)",
          overflow: "auto",
          maxHeight: "75vh",
          color: "var(--color-text)",
        }}
      >
        {txt}
      </pre>
    </div>
  );
}

// #005 — Inline save indicator (middle panel sticky tab bar'da).
// SaveIndicator'dan farkı: border-top yok, padding daha az, hata için renkli badge.
function SaveIndicatorInline({
  state,
  message,
}: {
  state: SaveState;
  message: string | null;
}): JSX.Element {
  const cfg: Record<SaveState, { color: string; icon: string; label: string }> = {
    idle: { color: "var(--color-text-3)", icon: "", label: "" },
    typing: { color: "var(--color-text-3)", icon: "•", label: "Yazılıyor…" },
    saving: { color: "var(--color-warning)", icon: "🔄", label: "Kaydediliyor…" },
    saved: { color: "var(--color-success)", icon: "✓", label: "Kaydedildi" },
    conflict: { color: "var(--color-error)", icon: "⚠", label: "Çakışma" },
    error: { color: "var(--color-error)", icon: "✕", label: "Hata" },
  };
  const c = cfg[state];
  if (state === "idle") return <span aria-hidden="true" />;
  return (
    <span
      className="text-xs"
      style={{
        color: c.color,
        display: "inline-flex",
        gap: 4,
        alignItems: "center",
        fontWeight: 500,
      }}
      role="status"
      aria-live="polite"
    >
      <span aria-hidden="true">{c.icon}</span>
      <span>{message ?? c.label}</span>
    </span>
  );
}

function SaveIndicator({
  state,
  message,
}: {
  state: SaveState;
  message: string | null;
}): JSX.Element {
  const cfg: Record<SaveState, { color: string; icon: string }> = {
    idle: { color: "transparent", icon: "" },
    typing: { color: "var(--color-text-3)", icon: "•" },
    saving: { color: "var(--color-warning)", icon: "⏳" },
    saved: { color: "var(--color-success)", icon: "✓" },
    conflict: { color: "var(--color-error)", icon: "!" },
    error: { color: "var(--color-error)", icon: "✕" },
  };
  const c = cfg[state];
  if (state === "idle") return <div style={{ height: 0 }} />;
  return (
    <div
      style={{
        padding: "6px 14px",
        borderTop: "1px solid var(--color-border)",
        fontSize: "0.75rem",
        color: c.color,
        display: "flex",
        gap: 6,
        alignItems: "center",
      }}
    >
      <span>{c.icon}</span>
      <span>{message ?? state}</span>
    </div>
  );
}

interface PublishDialogState {
  open: boolean;
  validation: PublishValidationResult | null;
  publishing: boolean;
  error: string | null;
  /** `null` = blok listesi henüz yüklenmedi; `[]` = sürümde `SEK-*` bölüm yok. */
  blocks: SectorBlock[] | null;
  /** İşaretli bölüm kodları — yayınla gövdesine bu gider. */
  selectedBlocks: string[];
}

interface PublishDialogProps {
  state: PublishDialogState;
  onClose: () => void;
  onConfirm: () => void;
  onToggleBlock: (sectionCode: string) => void;
}

function PublishDialog({
  state,
  onClose,
  onConfirm,
  onToggleBlock,
}: PublishDialogProps): JSX.Element {
  const ok = state.validation?.ok ?? false;
  const blocks = state.blocks ?? [];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(15,42,61,0.5)", zIndex: 55 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="rounded-xl p-6"
        style={{
          background: "var(--color-surface)",
          boxShadow: "var(--shadow-lg)",
          maxWidth: 520,
          width: "100%",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.3rem",
            color: "var(--color-text)",
            marginBottom: 12,
          }}
        >
          Çerçeveyi Yayınla
        </h2>

        {!state.validation ? (
          <p style={{ color: "var(--color-text-2)" }}>Doğrulama yapılıyor…</p>
        ) : ok ? (
          <div>
            <div
              style={{
                padding: "10px 14px",
                background: "color-mix(in srgb, var(--color-success) 10%, white)",
                borderRadius: 8,
                color: "var(--color-success)",
                fontSize: "0.9rem",
                marginBottom: 12,
              }}
            >
              ✓ Yayın kriterleri sağlandı
            </div>
            <div
              style={{
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: 12,
                fontSize: "0.85rem",
                color: "var(--color-text-2)",
              }}
            >
              <div>
                Bölüm: <strong>{state.validation.stats.sectionCount}</strong>
              </div>
              <div>
                Aktif soru: <strong>{state.validation.stats.questionCount}</strong>
              </div>
              <div>
                Zorunlu soru: <strong>{state.validation.stats.requiredQuestionCount}</strong>
              </div>
            </div>
            {/* run2 D2 — metin bayattı: auto-fork-on-publish #007 ile kaldırıldı, yayından
                sonra taslak DOĞMUYOR. Gerçek davranış yazılır. */}
            <p style={{ fontSize: "0.85rem", color: "var(--color-text-2)", marginTop: 12 }}>
              Yayınlandıktan sonra bu sürüm üzerinde değişiklik yapılamaz ve salt-okunur görünür.
              Değişiklik için “Yeni Versiyon (Fork)” ile yeni bir taslak açmanız gerekir.
            </p>

            {/* Sektör paketi türetme — yalnız `SEK-*` bölüm varsa. Varsayılan hepsi işaretli. */}
            {blocks.length > 0 ? (
              <fieldset
                style={{
                  marginTop: 12,
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  padding: "10px 12px",
                }}
              >
                <legend
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "var(--color-text)",
                    padding: "0 4px",
                  }}
                >
                  Sektör paketleri türet
                </legend>
                <p
                  style={{
                    fontSize: "0.78rem",
                    color: "var(--color-text-3)",
                    marginBottom: 8,
                    lineHeight: 1.5,
                  }}
                >
                  Her paket yayınlanır ve öteki sektör bölümlerini hariç tutar. İşareti
                  kaldırdığınız blok için paket açılmaz.
                </p>
                {blocks.map((b) => {
                  const others = blocks
                    .filter((o) => o.sectionCode !== b.sectionCode)
                    .map((o) => o.sectionCode);
                  return (
                    <label
                      key={b.sectionCode}
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                        fontSize: "0.85rem",
                        color: "var(--color-text)",
                        marginBottom: 6,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={state.selectedBlocks.includes(b.sectionCode)}
                        disabled={state.publishing}
                        onChange={() => onToggleBlock(b.sectionCode)}
                        style={{ marginTop: 3 }}
                      />
                      <span>
                        <strong>{b.packageName}</strong> paketi
                        {others.length > 0
                          ? `: ${others.join(", ")} hariç tutulur`
                          : ` — ${SECTOR_DERIVE_NO_EXCLUSION}`}
                        <span style={{ color: "var(--color-text-3)" }}> · {b.sectionCode}</span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            ) : null}
          </div>
        ) : (
          <div>
            <div
              style={{
                padding: "10px 14px",
                background: "color-mix(in srgb, var(--color-error) 10%, white)",
                borderRadius: 8,
                color: "var(--color-error)",
                fontSize: "0.9rem",
                marginBottom: 12,
              }}
            >
              ✕ Yayın kriterleri sağlanmadı
            </div>
            <ul style={{ paddingLeft: 20, color: "var(--color-error)", fontSize: "0.88rem" }}>
              {state.validation.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {state.error ? (
          <div
            style={{
              marginTop: 12,
              padding: "8px 12px",
              background: "color-mix(in srgb, var(--color-error) 8%, white)",
              color: "var(--color-error)",
              fontSize: "0.85rem",
              borderRadius: 6,
            }}
          >
            {state.error}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            disabled={state.publishing}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          >
            Kapat
          </button>
          {ok ? (
            <button
              onClick={onConfirm}
              disabled={state.publishing}
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              {state.publishing ? "Yayınlanıyor…" : "Yayınla"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ConflictModal(): JSX.Element {
  const load = useFormManagerStore((s) => s.load);
  const frameworkId = useFormManagerStore((s) => s.frameworkId);
  const versionId = useFormManagerStore((s) => s.versionId);
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(15,42,61,0.5)", zIndex: 60 }}
    >
      <div
        className="rounded-xl p-6"
        style={{
          background: "var(--color-surface)",
          boxShadow: "var(--shadow-lg)",
          maxWidth: 440,
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.2rem",
            color: "var(--color-error)",
            marginBottom: 8,
          }}
        >
          Çakışma
        </h2>
        <p style={{ color: "var(--color-text-2)", marginBottom: 16, fontSize: "0.9rem" }}>
          Bu çerçeve sizin gördüğünüz halinden sonra güncellenmiş (başka bir kullanıcı ya da aynı
          anda gönderilen ikinci bir işlem). Son işleminiz uygulanmadı. Değişikliklerinizin üzerine
          yazılmaması için güncel hali yükleyip son işlemi tekrarlayın.
        </p>
        <button
          onClick={() => {
            if (frameworkId && versionId) void load(frameworkId, versionId);
          }}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
        >
          Yenile
        </button>
      </div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div
      style={{
        maxWidth: 560,
        padding: "24px 28px",
        border: "1px solid var(--color-border)",
        borderRadius: 12,
        background: "var(--color-surface)",
      }}
    >
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "1.25rem",
          color: "var(--color-text)",
        }}
      >
        {title}
      </h2>
      <p style={{ color: "var(--color-text-2)", marginTop: 8, fontSize: "0.95rem" }}>{body}</p>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: "block" }}>
      <span
        style={{
          display: "block",
          fontSize: "0.75rem",
          color: "var(--color-text-3)",
          marginBottom: 4,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

const fieldInputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--color-border)",
  background: "var(--color-bg)",
  fontSize: "0.9rem",
  fontFamily: "var(--font-sans)",
  color: "var(--color-text)",
};

function toLocal(q: Question): QuestionPatch {
  return {
    code: q.code,
    label: q.label,
    description: q.description,
    helperText: q.helperText,
    options: q.options,
    required: q.required,
    enabled: q.enabled,
    priorYearHintDisabled: q.priorYearHintDisabled,
    allowEvidence: q.allowEvidence,
    allowNote: q.allowNote,
    aiContext: q.aiContext,
    // Dizi: kopyasını tut (mutasyon sızmasın), karşılaştırma JSON.stringify ile.
    applicablePeriods: [...q.applicablePeriods],
  };
}

function diffPatch(orig: Question, local: QuestionPatch): Partial<QuestionPatch> {
  const out: Partial<QuestionPatch> = {};
  if (local.code !== orig.code) out.code = local.code;
  if (local.label !== orig.label) out.label = local.label;
  if (local.description !== orig.description) out.description = local.description;
  if (local.helperText !== orig.helperText) out.helperText = local.helperText;
  if (JSON.stringify(local.options) !== JSON.stringify(orig.options)) out.options = local.options;
  if (local.required !== orig.required) out.required = local.required;
  if (local.enabled !== orig.enabled) out.enabled = local.enabled;
  if (local.priorYearHintDisabled !== orig.priorYearHintDisabled)
    out.priorYearHintDisabled = local.priorYearHintDisabled;
  if (local.allowEvidence !== orig.allowEvidence) out.allowEvidence = local.allowEvidence;
  if (local.allowNote !== orig.allowNote) out.allowNote = local.allowNote;
  if (local.aiContext !== orig.aiContext) out.aiContext = local.aiContext;
  if (JSON.stringify(local.applicablePeriods) !== JSON.stringify(orig.applicablePeriods))
    out.applicablePeriods = local.applicablePeriods;
  return out;
}

interface CreateModalProps {
  onClose: () => void;
  onSuccess: (createdId: string) => void | Promise<void>;
}

function CreateFrameworkModal({ onClose, onSuccess }: CreateModalProps): JSX.Element {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const fw = await createFramework({ name, description: description || undefined });
      await onSuccess(fw.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Oluşturulamadı");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(15,42,61,0.5)", zIndex: 50 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="w-full max-w-md rounded-xl p-6"
        style={{ background: "var(--color-surface)", boxShadow: "var(--shadow-lg)" }}
      >
        <h2
          className="text-xl mb-4"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
        >
          Yeni Çerçeve
        </h2>
        <label className="block mb-3">
          <span className="block text-sm mb-1">Ad</span>
          <input
            required
            minLength={2}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="örn. GRI 2026"
            className="w-full px-3 py-2 rounded-lg border"
            style={{ borderColor: "var(--color-border)" }}
          />
        </label>
        <label className="block mb-4">
          <span className="block text-sm mb-1">Açıklama (opsiyonel)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-lg border"
            style={{ borderColor: "var(--color-border)" }}
          />
        </label>
        <p style={{ fontSize: "0.8rem", color: "var(--color-text-3)", marginBottom: 12 }}>
          İlk taslak (v{new Date().getUTCFullYear()}.1) otomatik oluşacak.
        </p>
        {err ? (
          <div
            className="mb-3 px-3 py-2 rounded text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {err}
          </div>
        ) : null}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            {submitting ? "Oluşturuluyor…" : "Oluştur"}
          </button>
        </div>
      </form>
    </div>
  );
}
